// angular
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild
} from '@angular/core';
// stores
import { RestaurantStore } from 'src/app/core/stores/restaurant.store';
import { CartStore } from 'src/app/core/stores/cart.store';
// rxjs
import { Subject } from 'rxjs';
// models
import { NgModel } from '@angular/forms';
import { Platform } from '@ionic/angular';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { PaymentStore } from '../../../../core/stores/payment.store';
import { DeliveryType, TipType } from '../../../../core/models/order.model';
import { Tip } from '../../../../core/enums/tip.enum';
import { RestaurantAvailableStatus } from '../../../../core/models/restaurant.model';
import {SharedOrderService} from '../../../../core/services/shared-order.service';

@Component({
  selector: 'app-tipping',
  templateUrl: './tipping.component.html',
  styleUrls: ['./tipping.component.scss']
})
export class TippingComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild(NgModel, {static: true}) customAmountInput: NgModel;
  @ViewChild('customTip', {static: false, read: ElementRef})
  public customTipElementRef: ElementRef;
  public focusedCustomTipInput = false;

  private destroyed$ = new Subject<void>();
  private CUSTOM_ERROR_MESSAGE = 'Please enter a valid tip or enter $0 for no tip';
  private DEFAULT_ERROR_MESSAGE = 'Please choose a tip or select \"No Tip\"';
  private DEFAULT_NO_TIP = 'No tip';
  private valid = false;

  public customAmount;
  @Input()
  public items = [];
  @Input()
  public type = 'FLAT';
  @Input()
  public currencySign = '$';
  @Input()
  private smartTippingThreshold = 10;
  @Input()
  public useAutoGratuityPercent: boolean;
  @Input()
  public forceTouched;
  @Input()
  public closeTabPrice: number;
  @Input()
  public tipType: Tip = Tip.restaurant;
  @Input()
  public isDelivery: boolean;
  @Input()
  public deliveryType: DeliveryType|string;
  @Input()
  public availableRestaurant: RestaurantAvailableStatus = new RestaurantAvailableStatus();
  @Output()
  public validChange: EventEmitter<Boolean> = new EventEmitter<Boolean>(false);

  errorMessage = '';

  public selectedTip: number;
  public isShowCustomTipTextbox = false;
  private customAmountSubject = new Subject<number>();

  public get flexEndCss(): string {
    return this.platform.is('mobile') ? '2' : '1';
  }
  private get gratuityPercent(): number {
    return this.cartStore.getGratuityPercent(
      this.tipType,
      this.isDelivery,
      this.deliveryType,
      this.availableRestaurant.deliveryCourier,
      this.availableRestaurant.deliverySeller
    );
  }

  constructor(
    public restaurantStore: RestaurantStore,
    public paymentStore: PaymentStore,
    public cartStore: CartStore,
    private platform: Platform,
    private sharedOrderService: SharedOrderService
  ) {
  }

  ngOnInit() {
    this.customAmountSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe(value => {
      this.updateCustomTip(value);
    });
    this.customAmount = null;
    if (this.sharedOrderService.sharedOrderId$) {
      this.cartStore.setTip(null, Tip.restaurant);
      this.cartStore.setTip(null, Tip.driver);
      return;
    }
    if (this.cartStore.tippingEnabled) {
      if (!this.cartStore.tab && !this.cartStore.groupTab) {
        if (this.gratuityPercent !== 0) {
          this.isShowCustomTipTextbox = true;
          this.cartStore.items$
            .pipe(filter(() => this.useAutoGratuityPercent), takeUntil(this.destroyed$))
            .subscribe(() => {
              this.cartStore.setTip(
                this.roundTip((this.gratuityPercent / 100) *
                  (this.cartStore.totalPrice + this.cartStore.totalDiscountsPrice)),
                this.tipType
              );
              this.customAmount = this.cartStore.getTip(this.tipType);
            });
        } else {
          this.cartStore.setTip(0, this.tipType);
        }
      } else {
        this.cartStore.tip$
          .subscribe( (tip: number) => {
            if (tip) {
              this.customAmount = this.cartStore.getTip(this.tipType);
            }
          });
        this.cartStore.setTip(
          this.roundTip((this.gratuityPercent / 100) * this.closeTabPrice),
          this.tipType
        );
        this.customAmount = this.cartStore.getTip(this.tipType);
        this.focusCustomTip();
      }
      this.placeTip();
    } else {
      this.cartStore.setTip(0, this.tipType);
    }
  }

  ngOnChanges({forceTouched, useAutoGratuityPercent }: SimpleChanges): void {
    this.onForceTouchedChange(forceTouched);
    this.onUseAutoGratuityPercentChange(useAutoGratuityPercent);
  }

  @HostListener('unloaded')
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private onForceTouchedChange(forceTouched: SimpleChange): void {
    if (!forceTouched) {
      return;
    }
    const {previousValue: previousForceTouched, currentValue: currentForceTouched} = forceTouched;
    if (previousForceTouched !== currentForceTouched && currentForceTouched) {
      this.handleValidate();
    }
  }

  private onUseAutoGratuityPercentChange(useAutoGratuityPercent: SimpleChange): void {
    if (!useAutoGratuityPercent) {
      return;
    }
    const {previousValue, currentValue} = useAutoGratuityPercent;
    if (previousValue !== currentValue && !currentValue) {
      if (this.gratuityPercent > 0) {
          setTimeout(() => {
            this.selectCustomTip();
          }, 0);
        }
    }
  }

  public selectTip() {
    this.isShowCustomTipTextbox = false;
    this.customAmount = 0;
    this.placeTip();
  }

  public focusCustomTip(): void {
    this.focusedCustomTipInput = true;
    this.isShowCustomTipTextbox = true;
    this.selectedTip = null;
    this.cartStore.setTip(this.customAmount, this.tipType);
  }

  public selectCustomTip() {
    this.customTipElementRef.nativeElement.focus();
  }

  public blurCustomTip() {
    this.focusedCustomTipInput = false;
  }

  public isCustomTipError(): boolean {
    const customTip = this.getTipAmount(this.customAmount);
    if (this.customAmount != null && customTip >= 0 && customTip <= (100 * 100)) {
      this.resetError();
      return false;
    } else if (!this.customTipElementRef.nativeElement.value) {
      this.cartStore.setTip(null, this.tipType);
      this.handleError(this.CUSTOM_ERROR_MESSAGE);
      return true;
    }
  }

  public isDefaultTipError(): boolean {
    if (this.selectedTip != null && this.roundTip(this.selectedTip) >= 0 && this.roundTip(this.selectedTip) <= (100 * 100)) {
      this.resetError();
      return false;
    } else {
      this.cartStore.setTip(null, this.tipType);
      this.handleError(this.DEFAULT_ERROR_MESSAGE);
      return true;
    }
  }

  private handleValidate(): boolean {
    if (this.isShowCustomTipTextbox) {
      return this.isCustomTipError();
    }
    return this.isDefaultTipError();
  }

  public showCustomTipSelectedStyle(checked = false, hover = false): boolean {
    return checked || hover || this.isShowCustomTipTextbox;
  }

  public formatTip(tip: string): string {
    if (this.type === TipType.FLAT) {
      return tip ? `${this.currencySign}${tip}` : this.DEFAULT_NO_TIP;
    } else if (this.type === TipType.PERCENTAGE) {
      return tip ? `${tip}%` : this.DEFAULT_NO_TIP;
    } else if (this.type === TipType.SMART) {
      if (this.cartStore.totalPrice < this.smartTippingThreshold) {
        return tip ? `${this.currencySign}${tip}` : this.DEFAULT_NO_TIP;
      } else {
        return tip ? `${tip}%` : this.DEFAULT_NO_TIP;
      }
    }
    return tip ? `${this.currencySign}${tip}` : this.DEFAULT_NO_TIP;
  }

  public placeTip() {
    if (this.selectedTip == null && !this.customAmount && !this.focusedCustomTipInput) {
      return;
    }

    if (this.isShowCustomTipTextbox) {
      if (!this.isCustomTipError()) {
        const customTipInCents = this.getTipAmount(this.customAmount);
        if (customTipInCents >= 0 && customTipInCents <= (500 * 100)) {
          this.cartStore.setTip(this.roundTip(customTipInCents), this.tipType);
        } else {
          this.handleError(this.CUSTOM_ERROR_MESSAGE);
          this.customAmountInput.control.markAsTouched();
          return;
        }
      }
    } else {
      if (!this.isDefaultTipError()) {
        if (this.type === TipType.FLAT) {
          this.cartStore.setTip(this.roundTip(this.selectedTip), this.tipType);
        } else if (this.type === TipType.PERCENTAGE) {
          this.cartStore.setTip(this.roundTip(this.cartStore.totalPrice * this.selectedTip / 100), this.tipType);
        } else if (this.type === TipType.SMART) {
          if (this.cartStore.totalPrice < this.smartTippingThreshold) {
            this.cartStore.setTip(this.roundTip(this.selectedTip), this.tipType);
          } else {
            this.cartStore.setTip(this.roundTip(this.cartStore.totalPrice * this.selectedTip / 100), this.tipType);
          }
        }
      }
    }
  }

  private handleError(message: string): void {
    this.errorMessage = message;
    if (this.valid) {
      this.validChange.emit(false);
    }
    this.valid = false;
  }

  private resetError(): void {
    this.errorMessage = '';
    if (!this.valid) {
      this.validChange.emit(true);
    }
    this.valid = true;
  }

  private roundTip(amount): number {
    return this.getTipAmount(amount.toFixed(2));
  }

  private getTipAmount(amount: string): number {
    const x = amount;
    const y = +x;
    return y;
  }

  public onCustomAmountChange(event: any): void {
    const value = event.target.value ? parseFloat(event.target.value) : 0;
    this.customAmountSubject.next(value);
  }

  private updateCustomTip(value: number): void {
    if (value >= 0 && value <= 500) {
      this.customAmount = value;
      this.placeTip()
    } else {
    }
  }
}
